import { useState, useMemo } from "react";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { applyBarSoftScale } from "../../helpers/chart/ChartSoftScale";
import { resolveColor } from "../../helpers/chart/ChartColors";
import Info from "~components/Form/components/Info";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

const numberFormatter = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 });
const formatChartValue = (value) => value == null ? "" : numberFormatter.format(value);

const buildDatasets = ({ data, setData = () => {}, hidden }) => {
  const datasetsMap = {};
  const series = {};
  const points = {};

  data.forEach((month, monthIndex) => {
    points[monthIndex] ??= month.media;

    Object.values(month.groups).forEach((group, index) => {
      const key = group.slug;

      if(hidden?.[key] == undefined || !hidden[key]) {

        if (!datasetsMap[key]) {
          datasetsMap[key] = {
            label: group.label,
            backgroundColor: resolveColor(key, index) ?? "",
            data: Array(data.length).fill(null),
            borderRadius: 2,
            stack: "volume",
            type: "bar",
            yAxisID: "y",
            order: 2,
            barPercentage: 0.55,
            categoryPercentage: 0.7,
            datalabels: {
              anchor: "center",
              align: "center",
              color: "#fff",
              font: { size: 10, weight: "bold" },
              formatter: (_, context) => formatChartValue(context.dataset.originalData[context.dataIndex]),
            },
          };
        }

        datasetsMap[key].data[monthIndex] = group.volume;
      }

      series[key] ??= {
        color: resolveColor(key, index) ?? "",
        label: group.label
      };
    });
  });


  const datasets = Object.values(datasetsMap);

  const validIndexes = data
    .map((_, index) =>
      datasets.some(ds => ds.data[index] > 0)
    );

  datasets.forEach(ds => {
    ds.data = ds.data.filter((_, index) => validIndexes[index]);
  });

  const mediaPoints = Object.values(points)
    .filter((_, index) => validIndexes[index]);

  const labels = data
    .filter((_, index) => validIndexes[index])
    .map(e => e.label);

  setData?.(series);

  const mediaHidden = hidden['media'] ?? false;
  const line = {
    type: "line",
    label: "Média de volume faturado/negociado",
    data: mediaPoints,
    borderColor: mediaHidden ? "transparent" : "#000",
    backgroundColor: mediaHidden ? "transparent" : "#000",
    borderWidth: 2,
    pointRadius: mediaHidden ? 0 : 3,
    pointBackgroundColor: "#000",
    tension: 0,
    yAxisID: "y2",
    order: 1,
    datalabels: {
      align: "top",
      anchor: "end",
      color: "#01054C",
      font: { size: 11, weight: "bold" },
      formatter: (value) => mediaHidden || value == null ? "" : `${formatChartValue(value)}%`
    },
  };

  return {
    labels,
    datasets: applyBarSoftScale([
      line,
      ...datasets,
    ], data),
  };
};

const rightMax = 100;

const computeLeftMax = (data) => {
  let maxRatio = 0;
  data.forEach((d) => {
    if (d.media) {
      const barTotal = Object.entries(d.groups).reduce((acc, [k, v]) => acc + (v.volume ?? 0), 0);
      const ratio = barTotal / d.media;
      if (ratio > maxRatio) maxRatio = ratio;
    }
  });
  const raw = maxRatio * rightMax;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw || 1)));
  const step = magnitude;
  return Math.ceil(raw / step) * step || 25000;
};

const buildChartOptions = (leftMax, labels, labelSums) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: true,
      callbacks: {
        label: ({ dataset, dataIndex, parsed }) =>
          `${dataset.label}: ${formatChartValue(dataset.originalData?.[dataIndex] ?? parsed.y)}${dataset.type === "line" ? "%" : ""}`,
      },
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      ticks: {
        font: { size: 11 },
        color: "#01054C",
        callback: function (_, index) {
          const label = labels[index];
          const total = labelSums[index] ?? 0;

          return [
            label,
            "",
            total ? formatChartValue(total) : "",
            total ? "────" : "",
          ];
        },
      },
    },
    y: {
      display: false,
      stacked: true,
      position: "left",
      beginAtZero: true,
      grace: "5%",
      grid: { color: "#e5e7eb" },
      ticks: { font: { size: 10 }, color: "#01054C", stepSize: leftMax / 5, callback: formatChartValue },
    },
    y2: {
      display: false,
      position: "right",
      grace: '15%',
      suggestedMax: 100,
      beginAtZero: true,
      grid: { display: false },
      ticks: { font: { size: 10 }, color: "#01054C", stepSize: 5, callback: formatChartValue },
    },
  },
  layout: { padding: { top: 20 } },
});

const ActiveWorkChart = ({ data, productGroup = '' }) => {
  const [hidden, setHidden] = useState({});
  const [series, setSeries] = useState([]);

  const toggle = (key) => setHidden((prev) => ({ ...prev, [key]: !prev[key] }));

  const chartData = useMemo(() => {
    const { labels, datasets } = buildDatasets({ data, setData: setSeries, hidden });
    return {
      labels,
      datasets
    }
  }, [data, hidden]);

  const labelSums = useMemo(() => {
    return chartData.labels.map((_, index) =>
      chartData.datasets.reduce((acc, dataset) => {
        if (dataset.type === "line") return acc;
        return acc + (dataset.originalData[index] || 0);
      }, 0)
    );
  }, [chartData]);

  const leftMax = computeLeftMax(data);

  return (
    <div className="bg-white rounded-xl shadow px-6 pt-5 pb-4 mt-6">
      <h3 className="relative flex justify-center items-center gap-1 text-center font-bold text-base text-primaria mb-4">
        Volumes Faturados
        <Info
          bg="blue"
          position="mobile-left"
          text="Considera somatória dos volumes dos últimos 6M"
          tooltipClass="w-6 h-6 relative"
        />
      </h3>
      <div style={{ height: 320 }}>
        <Chart type="bar" data={chartData} options={buildChartOptions(leftMax, chartData.labels, labelSums)} />
      </div>
      <div className="grid grid-cols-[repeat(4,auto)] justify-start gap-y-4 gap-x-[30px] mt-4 pl-[40px]">
        {Object.entries(series).map(([k, { label, color }]) => (
          <button
            key={k}
            type="button"
            onClick={() => toggle(k)}
            className="flex items-center gap-1.5"
            style={{ opacity: hidden[k] ? 0.35 : 1 }}
          >
            <span
              className="inline-block w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: color }}
            />
            <span className="text-xs">{label}</span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => toggle("media")}
          className="flex items-center gap-1.5"
          style={{ opacity: hidden["media"] ? 0.35 : 1 }}
        >
          <span
            className="inline-block w-8 h-[2px] flex-shrink-0"
            style={{ backgroundColor: "#000" }}
          />
          <span className="text-xs">Média de volume faturado/negociado</span>
        </button>
      </div>
    </div>
  );
};

export default ActiveWorkChart;